import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageCircle,
  ChevronDown,
  Search,
  BadgeCheck,
  AlertCircle,
  AlertTriangle,
  Info,
} from "lucide-react";

import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { submitLeadWithWhatsApp } from "./lib/whatsappHandoff";
import { toast } from "./components/ui/sonner";

import SEED from "./data/blinkitCategorySeed.json";

/* -----------------------------------------------------------------------------
 * /guides/blinkit-commission-structure
 *
 * Real, indexable guide replacing the previous <GuideStub />. Ships the
 * interactive Blinkit commission calculator, an "Understanding Blinkit
 * commissions" explainer, an FAQ (mirrored into FAQPage schema), and a
 * contact block.
 *
 * All rates and constants come from ./data/blinkitCategorySeed.json.
 * The `_source` field is used to drive the "Verified rates" vs
 * "Estimated (4% default)" badge on the results card. `_note` is
 * internal only and never rendered.
 * ---------------------------------------------------------------------------*/

const CANONICAL = "https://letspickquick.com/guides/blinkit-commission-structure";
const WHATSAPP_NUMBER = "918733085863";
const CATEGORIES = SEED.categories;
const {
  gstPct,
  inwardingFeePerUnit,
  fulfillmentFeePerOrder,
  storageMinPerUnitPerDay,
  otherCharges,
} = SEED;

/* ============================================================================
 * PURE CALCULATION HELPERS
 * ==========================================================================*/

// Boundary rule: a price sitting exactly on the max of one slab and the min
// of the next takes the LOWER band. Encoded as: SP > slab.min (with the
// special case SP >= 0 for the first, zero-min slab) AND SP <= slab.max.
export function findActiveSlab(slabs, sp) {
  for (const s of slabs) {
    const lowerOk = s.min === 0 ? sp >= 0 : sp > s.min;
    const upperOk = s.max === null || s.max === undefined ? true : sp <= s.max;
    if (lowerOk && upperOk) return s;
  }
  return null;
}

// Volumetric storage, bracketed by age. Within each bracket we compare the
// volumetric cost against the flat per-day floor and take the higher, then
// sum across brackets that overlap [1, daysToSell]. Within a bracket the
// per-day rate is constant so this is equivalent to per-day maxing.
export function computeStorage(brackets, volumeCft, daysToSell, floorPerDay) {
  let total = 0;
  for (const b of brackets) {
    const ageFrom = b.ageFromDay;
    const ageTo = b.ageToDay === null || b.ageToDay === undefined ? Infinity : b.ageToDay;
    const overlapStart = Math.max(1, ageFrom);
    const overlapEnd = Math.min(daysToSell, ageTo);
    const daysInBracket = overlapEnd - overlapStart + 1;
    if (daysInBracket <= 0) continue;
    const volumetric = b.perCubicFtPerDay * volumeCft * daysInBracket;
    const floor = floorPerDay * daysInBracket;
    total += Math.max(volumetric, floor);
  }
  return total;
}

export function computeBreakdown({ category, sp, length, breadth, height, days, quantity }) {
  if (!category || !(sp > 0) || !(quantity >= 1) || !(days >= 1)) return null;

  const slab = findActiveSlab(category.commissionSlabs, sp);
  const commissionPct = slab ? slab.pct : 0;
  const categoryCommissionPerUnit = (sp * commissionPct) / 100;
  const categoryCommission = categoryCommissionPerUnit * quantity;

  const inwardingFee = inwardingFeePerUnit * quantity;

  const L = Math.max(0, length);
  const B = Math.max(0, breadth);
  const H = Math.max(0, height);
  const volumeCubicFt = (L * B * H) / 28316;
  const storagePerUnit = computeStorage(
    category.storageBrackets,
    volumeCubicFt,
    days,
    storageMinPerUnitPerDay
  );
  const storageFee = storagePerUnit * quantity;

  const fulfillmentFee = fulfillmentFeePerOrder; // per order, not per unit

  const totalPreGst = inwardingFee + storageFee + fulfillmentFee + categoryCommission;
  const gstAmount = (totalPreGst * gstPct) / 100;
  const totalPostGst = totalPreGst + gstAmount;

  const viabilityBreached = totalPreGst > 0.6 * sp;

  // Display-only "other charges", never added to the total.
  const inventoryRecall = otherCharges.inventoryRecallPerUnit * quantity;
  const customerReturn = otherCharges.customerReturnPerQty * quantity;

  return {
    slab,
    commissionPct,
    categoryCommission,
    inwardingFee,
    volumeCubicFt,
    storagePerUnit,
    storageFee,
    fulfillmentFee,
    totalPreGst,
    gstAmount,
    totalPostGst,
    viabilityBreached,
    inventoryRecall,
    customerReturn,
    lossCompensationNote: otherCharges.lossCompensationNote,
  };
}

/* ============================================================================
 * FORMATTING
 * ==========================================================================*/

const INR = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtINR = (n) => (Number.isFinite(n) ? `₹${INR.format(n)}` : "—");

/* ============================================================================
 * STATIC CONTENT
 * ==========================================================================*/

const EXPLAINER = [
  {
    q: "Category commission",
    a: "Blinkit charges a percentage of the selling price. That percentage depends on your category and, in most non-grocery categories, on the price slab of the SKU. Higher price bands typically carry a higher commission percentage.",
  },
  {
    q: "Inwarding fee",
    a: "A flat ₹5 per unit is charged when your stock is received into the Blinkit warehouse or dark store. On a 10 unit order that is ₹50 in inwarding.",
  },
  {
    q: "Storage fee",
    a: "Storage is volumetric, priced per cubic foot per day. Volume is length × breadth × height in centimetres divided by 28,316 to convert to cubic feet. A minimum floor of ₹1 per unit per day applies, so very small SKUs still pay a per day minimum. The rate rises after day 30 and again after day 60, so slow moving SKUs cost more to store.",
  },
  {
    q: "Fulfillment fee",
    a: "A flat ₹50 per customer order, not per unit. If a customer orders 3 units in one basket, one ₹50 fulfillment fee applies to the order.",
  },
  {
    q: "GST",
    a: "18% GST is charged on the sum of category commission, inwarding, storage and fulfillment. The calculator surfaces this both as a line item and as the total including GST.",
  },
  {
    q: "Other charges",
    a: "Inventory recall is ₹5 per unit when stock is returned from a dark store. Customer returns are ₹50 per quantity. Loss compensation is the lower of your landed price or 60% of the average selling price. These are shown for planning and are not added to the running total.",
  },
];

const FAQS = [
  {
    q: "How is Blinkit's commission calculated?",
    a: "Blinkit's total platform take is the sum of four charges plus GST: a category commission which is a percentage of your selling price, a ₹5 per unit inwarding fee, a volumetric storage fee based on your SKU's dimensions and how long it sits in the dark store, and a flat ₹50 fulfillment fee per customer order. 18% GST is then applied to the sum of those four.",
  },
  {
    q: "Does Blinkit commission vary by category?",
    a: "Yes. Each category has its own commission structure. Most categories additionally use price slabs, so the same SKU at a higher price point can move into a higher commission band. This calculator applies the category and slab for you.",
  },
  {
    q: "What other fees does Blinkit charge besides commission?",
    a: "Beyond the four core charges (commission, inwarding, storage, fulfillment) and 18% GST, Blinkit charges ₹5 per unit for inventory recall from a dark store, ₹50 per quantity on customer returns, and applies a loss compensation policy of the lower of your landed price or 60% of the average selling price. These are shown as \"Other charges\" and are not included in the running total in this calculator.",
  },
  {
    q: "Why does my product show as \"not viable\"?",
    a: "If Blinkit's total pre-GST take is more than 60% of your selling price, the calculator flags it as not viable to sell on Blinkit at that price in that category. Typically this happens when a small, low-priced SKU carries a high category commission and the fixed fees (inwarding and fulfillment) crowd out the unit economics. The fix is usually a higher price, a bigger pack, or a different category placement.",
  },
  {
    q: "Are these commission rates official?",
    a: "The verified rates are read from Blinkit's published commission structure and are marked \"Verified\" on the results card. Some categories are still marked \"Estimated\" and default to a placeholder 4% while we finalise the exact bands. For exact commercials on your specific SKUs, book a free audit and PickQuick's operators will walk you through them.",
  },
];

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Blinkit commission structure and calculator, by PickQuick",
  serviceType: "Quick commerce commission analysis and category pricing",
  provider: {
    "@type": "Organization",
    name: "PickQuick",
    legalName: "Civicgrid Technologies Pvt. Ltd.",
    url: "https://letspickquick.com",
  },
  areaServed: { "@type": "Country", "name": "India" },
  url: CANONICAL,
  description:
    "Interactive calculator that computes Blinkit's total platform take (category commission, inwarding, volumetric storage, fulfillment and 18% GST) for any FMCG SKU across 128 Blinkit categories.",
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", "text": a },
  })),
};

/* ============================================================================
 * SEARCHABLE CATEGORY DROPDOWN
 * ==========================================================================*/

const CategoryPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = CATEGORIES.find((c) => c.id === value);
  const filtered = query.trim()
    ? CATEGORIES.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
    : CATEGORIES;

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="calc-category-trigger"
        className="w-full flex items-center justify-between"
        style={{
          height: "46px",
          padding: "0 14px",
          backgroundColor: "#0F0F0F",
          border: "1px solid #262626",
          borderRadius: "8px",
          color: selected ? "#FFFFFF" : "#8A8A8A",
          fontSize: "15px",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : "Select category"}
        </span>
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "#8A8A8A" }} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-30 mt-2 rounded-lg overflow-hidden"
          style={{ backgroundColor: "#0F0F0F", border: "1px solid #262626", maxHeight: "320px", display: "flex", flexDirection: "column" }}
        >
          <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: "1px solid #262626" }}>
            <Search className="w-4 h-4" style={{ color: "#8A8A8A" }} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 128 categories..."
              data-testid="calc-category-search"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "#FFFFFF",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ overflowY: "auto" }}>
            {filtered.length === 0 && (
              <div style={{ padding: "16px", color: "#8A8A8A", fontSize: "14px" }}>
                No categories match "{query}".
              </div>
            )}
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onChange(c.id);
                  setOpen(false);
                  setQuery("");
                }}
                data-testid={`calc-category-option-${c.id}`}
                className="w-full text-left flex items-center justify-between"
                style={{
                  padding: "10px 14px",
                  color: c.id === value ? "#F1592B" : "#E8E8E8",
                  fontSize: "14px",
                  background: c.id === value ? "rgba(241,89,43,0.08)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = c.id === value ? "rgba(241,89,43,0.08)" : "transparent")}
              >
                <span>{c.label}</span>
                {c._source === "verified" && (
                  <span
                    aria-label="Verified rate"
                    title="Verified rates"
                    style={{ fontSize: "11px", color: "#4ADE80", fontWeight: 600 }}
                  >
                    ✓ verified
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * PAGE COMPONENT
 * ==========================================================================*/

const inputStyle = {
  width: "100%",
  height: "46px",
  padding: "0 14px",
  backgroundColor: "#0F0F0F",
  border: "1px solid #262626",
  borderRadius: "8px",
  color: "#FFFFFF",
  fontSize: "15px",
  outline: "none",
};

const primaryCtaStyle = {
  backgroundColor: "#F1592B",
  color: "#FFFFFF",
  height: "52px",
  padding: "0 26px",
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  borderRadius: "10px",
  fontSize: "15.5px",
  fontWeight: 600,
  border: "none",
  cursor: "pointer",
  textDecoration: "none",
};

const EyebrowLabel = ({ children }) => (
  <p
    className="text-xs font-semibold uppercase mb-4"
    style={{ color: "#F1592B", letterSpacing: "0.18em" }}
  >
    {children}
  </p>
);

const SubheadingLabel = ({ children }) => (
  <p style={{ fontSize: "12px", color: "#8A8A8A", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700 }}>
    {children}
  </p>
);

const BlinkitCommissionStructure = () => {
  useDocumentMeta({
    title: "Blinkit Commission Structure & Calculator (2026) | PickQuick",
    description:
      "Calculate your exact Blinkit commission, GST, storage and fulfilment fees by category and price. Free Blinkit commission calculator for FMCG brands. Book a free audit.",
    ogType: "article",
    ogUrl: CANONICAL,
    canonical: CANONICAL,
    keywords: "blinkit commission, blinkit commission calculator, blinkit fees, blinkit category commission, blinkit storage fee, blinkit fulfillment fee, quick commerce commission india, PickQuick",
    schema: [SERVICE_SCHEMA, FAQ_SCHEMA],
  });

  // Raw form state, edited every keystroke.
  const [rawInputs, setRawInputs] = useState({
    categoryId: "",
    priceStr: "",
    lengthStr: "1",
    breadthStr: "1",
    heightStr: "1",
    days: 30,
    quantityStr: "1",
  });

  // Committed inputs, updated on a 150ms debounce so heavy renders don't
  // fire on every keystroke.
  const [inputs, setInputs] = useState(rawInputs);
  useEffect(() => {
    const t = setTimeout(() => setInputs(rawInputs), 150);
    return () => clearTimeout(t);
  }, [rawInputs]);

  const patch = (partial) => setRawInputs((prev) => ({ ...prev, ...partial }));

  // Parse committed values.
  const category = useMemo(
    () => CATEGORIES.find((c) => c.id === inputs.categoryId) || null,
    [inputs.categoryId]
  );
  const sp = parseFloat(inputs.priceStr);
  const length = parseFloat(inputs.lengthStr);
  const breadth = parseFloat(inputs.breadthStr);
  const height = parseFloat(inputs.heightStr);
  const quantity = parseInt(inputs.quantityStr, 10);
  const days = inputs.days;

  const priceValid = Number.isFinite(sp) && sp > 0;
  const priceProvided = inputs.priceStr.trim().length > 0;
  const priceError = priceProvided && !priceValid ? "Selling price must be greater than 0." : "";

  const breakdown = useMemo(() => {
    if (!category || !priceValid) return null;
    return computeBreakdown({
      category,
      sp,
      length: Number.isFinite(length) && length > 0 ? length : 0,
      breadth: Number.isFinite(breadth) && breadth > 0 ? breadth : 0,
      height: Number.isFinite(height) && height > 0 ? height : 0,
      days: Number.isFinite(days) && days > 0 ? days : 1,
      quantity: Number.isFinite(quantity) && quantity >= 1 ? quantity : 1,
    });
  }, [category, priceValid, sp, length, breadth, height, days, quantity]);

  const viabilityInlineError =
    breakdown?.viabilityBreached
      ? "Platform commission is more than 60% of the selling price."
      : "";

  // Expandable panels
  const [showSlabTable, setShowSlabTable] = useState(false);
  const [showStorageTable, setShowStorageTable] = useState(false);

  /* ------------- Contact form (WhatsApp handoff) ---------------- */
  const [contact, setContact] = useState({ name: "", brand: "", email: "", phone: "" });
  const setContactField = (f) => (e) => setContact((prev) => ({ ...prev, [f]: e.target.value }));
  const sendOnWhatsApp = useCallback(() => {
    if (!contact.name.trim() || !contact.brand.trim() || !contact.email.trim()) {
      toast.error("Name, brand and work email are required.");
      return;
    }
    submitLeadWithWhatsApp({
      payload: {
        name: contact.name.trim(),
        brand: contact.brand.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim(),
        context:
          category && priceValid
            ? `Blinkit commission audit request. Category: ${category.label}, SP: ₹${sp}, Days: ${days}, Qty: ${quantity}`
            : "Blinkit commission audit request",
        lead_type: "blinkit-commission-calculator",
        source: "blinkit-commission-calculator-form",
      },
      whatsapp: {
        title: "New PickQuick Blinkit audit enquiry",
        source: "blinkit-commission-calculator-form",
        pagePath: "/guides/blinkit-commission-structure",
        fields: {
          Name: contact.name.trim(),
          Brand: contact.brand.trim(),
          "Work email": contact.email.trim(),
          Phone: contact.phone.trim() || "(not provided)",
          "From calculator": category ? `${category.label} at ₹${sp}` : "(no SKU entered yet)",
        },
      },
    });
    toast.success("Opening WhatsApp with your details, we will pick up from there.");
    setContact({ name: "", brand: "", email: "", phone: "" });
  }, [contact, category, priceValid, sp, days, quantity]);

  const openAuditWhatsApp = () => {
    submitLeadWithWhatsApp({
      payload: {
        name: "(from calculator)",
        brand: "(unknown)",
        email: "(unknown)",
        phone: "",
        context:
          category && priceValid
            ? `Free audit request from Blinkit commission calculator. Category: ${category.label}, SP: ₹${sp}, Days: ${days}, Qty: ${quantity}`
            : "Free audit request from Blinkit commission calculator",
        lead_type: "blinkit-commission-calculator",
        source: "blinkit-calc-book-audit-cta",
      },
      whatsapp: {
        title: "Free Blinkit audit request",
        source: "blinkit-calc-book-audit-cta",
        pagePath: "/guides/blinkit-commission-structure",
        fields: {
          "From calculator": category ? `${category.label} at ₹${sp || "—"}` : "(no SKU entered)",
        },
      },
    });
  };

  const badge = category
    ? category._source === "verified"
      ? { label: "Verified rates", color: "#4ADE80", bg: "rgba(74,222,128,0.12)" }
      : { label: "Estimated (4% default)", color: "#FBBF24", bg: "rgba(251,191,36,0.12)" }
    : null;

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-white flex flex-col">
      <SiteHeader />

      {/* Floating WhatsApp button */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with PickQuick on WhatsApp"
        className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] shadow-lg hover:shadow-xl transition-shadow duration-200"
      >
        <MessageCircle className="h-6 w-6 text-white" />
      </a>

      <main className="flex-1">
        {/* ============ EXISTING HERO: eyebrow + H1 + intro ============ */}
        <section className="pt-24 pb-14 md:pt-28 md:pb-16" style={{ background: "#000000" }} data-testid="calc-hero">
          <div className="container">
            <div className="max-w-[1080px]">
              <EyebrowLabel>Guide</EyebrowLabel>
              <h1
                className="font-bold text-white"
                data-testid="calc-h1"
                style={{ fontSize: "clamp(30px, 4vw, 48px)", lineHeight: 1.1, letterSpacing: "-0.02em" }}
              >
                Blinkit commission <span style={{ color: "#F1592B" }}>structure</span>.
              </h1>
              <p
                className="mt-6 max-w-[820px]"
                style={{ color: "#B8B8B8", fontSize: "17px", lineHeight: 1.65 }}
              >
                Blinkit&apos;s commission varies by category and price slab, plus fixed inwarding, storage and fulfilment fees and 18% GST. This calculator computes the total platform take on any SKU. All rates come from Blinkit&apos;s published commission structure; some category bands are still indicative estimates and are marked as such.
              </p>
            </div>
          </div>
        </section>

        {/* ============ CALCULATOR ============ */}
        <section className="pb-20" style={{ background: "#000000" }} data-testid="calc-section">
          <div className="container">
            <div className="grid gap-6 lg:gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
              {/* ---------------- INPUTS COLUMN ---------------- */}
              <div
                className="rounded-2xl p-6 md:p-7"
                style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
                data-testid="calc-inputs"
              >
                <SubheadingLabel>Define your product</SubheadingLabel>

                <div className="mt-5 space-y-5">
                  <div>
                    <label className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Business category
                    </label>
                    <CategoryPicker
                      value={inputs.categoryId}
                      onChange={(id) => patch({ categoryId: id })}
                    />
                    {category && category._source !== "verified" && (
                      <p className="mt-2 flex items-center gap-1.5" style={{ fontSize: "12px", color: "#FBBF24" }}>
                        <Info className="w-3.5 h-3.5" />
                        Rates for this category are indicative estimates.
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="calc-price" className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Selling price (₹)
                    </label>
                    <input
                      id="calc-price"
                      data-testid="calc-price"
                      inputMode="decimal"
                      value={rawInputs.priceStr}
                      onChange={(e) => patch({ priceStr: e.target.value.replace(/[^0-9.]/g, "") })}
                      placeholder="e.g. 249.00"
                      style={inputStyle}
                    />
                    {(priceError || viabilityInlineError) && (
                      <p
                        className="mt-2 flex items-center gap-1.5"
                        style={{ fontSize: "12px", color: viabilityInlineError ? "#FBBF24" : "#F87171" }}
                        data-testid="calc-price-error"
                      >
                        <AlertCircle className="w-3.5 h-3.5" />
                        {viabilityInlineError || priceError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Dimensions L × B × H (cm)
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: "lengthStr", label: "L", testid: "calc-length" },
                        { key: "breadthStr", label: "B", testid: "calc-breadth" },
                        { key: "heightStr", label: "H", testid: "calc-height" },
                      ].map((f) => (
                        <div key={f.key} className="relative">
                          <span
                            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                            style={{ color: "#8A8A8A", fontSize: "12px", fontWeight: 700 }}
                          >
                            {f.label}
                          </span>
                          <input
                            data-testid={f.testid}
                            inputMode="decimal"
                            value={rawInputs[f.key]}
                            onChange={(e) => patch({ [f.key]: e.target.value.replace(/[^0-9.]/g, "") })}
                            style={{ ...inputStyle, paddingLeft: "28px" }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block" style={{ fontSize: "13px", color: "#B8B8B8" }}>
                        Expected days to sell
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        max="180"
                        step="1"
                        data-testid="calc-days-input"
                        value={rawInputs.days}
                        onChange={(e) => {
                          const v = parseInt(e.target.value || "0", 10);
                          const clamped = Number.isFinite(v) ? Math.max(1, Math.min(180, v)) : 1;
                          patch({ days: clamped });
                        }}
                        style={{ ...inputStyle, width: "88px", textAlign: "center" }}
                      />
                      <div className="flex-1 relative">
                        <input
                          type="range"
                          min="1"
                          max="180"
                          step="1"
                          data-testid="calc-days-slider"
                          value={rawInputs.days}
                          onChange={(e) => patch({ days: parseInt(e.target.value, 10) })}
                          className="w-full"
                          style={{ accentColor: "#F1592B" }}
                          aria-label={`Expected days to sell: ${rawInputs.days} days`}
                          title={`${rawInputs.days} days`}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between mt-1" style={{ fontSize: "11px", color: "#6B6B6B" }}>
                      <span>1 day</span>
                      <span>180 days</span>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="calc-qty" className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Quantity
                    </label>
                    <input
                      id="calc-qty"
                      data-testid="calc-quantity"
                      inputMode="numeric"
                      value={rawInputs.quantityStr}
                      onChange={(e) => patch({ quantityStr: e.target.value.replace(/[^0-9]/g, "") })}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* ---------------- RESULTS COLUMN (green card) ---------------- */}
              <div
                className="rounded-2xl p-6 md:p-7"
                style={{
                  background: "linear-gradient(180deg, #0E2118 0%, #0A1A12 100%)",
                  border: "1px solid #1E3A28",
                }}
                data-testid="calc-results"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <SubheadingLabel>
                    <span style={{ color: "#4ADE80" }}>Blinkit</span> commission breakdown
                  </SubheadingLabel>
                  {badge && (
                    <span
                      data-testid="calc-badge"
                      className="inline-flex items-center gap-1.5"
                      style={{
                        fontSize: "11.5px",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: badge.color,
                        backgroundColor: badge.bg,
                        padding: "5px 10px",
                        borderRadius: "999px",
                      }}
                    >
                      <BadgeCheck className="w-3.5 h-3.5" />
                      {badge.label}
                    </span>
                  )}
                </div>

                {breakdown?.viabilityBreached && (
                  <div
                    className="mt-5 rounded-lg p-4 flex items-start gap-3"
                    style={{ backgroundColor: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.35)" }}
                    data-testid="calc-viability-banner"
                  >
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#FBBF24" }} />
                    <p style={{ color: "#FDE68A", fontSize: "14px", lineHeight: 1.5 }}>
                      This category at this price is not viable to sell on Blinkit.
                    </p>
                  </div>
                )}

                {!breakdown && (
                  <div className="mt-8" data-testid="calc-empty">
                    <p style={{ color: "#8A8A8A", fontSize: "14.5px", lineHeight: 1.6 }}>
                      Pick a category and enter a selling price to see the full breakdown.
                    </p>
                    <p className="mt-6" style={{ color: "#4ADE80", fontSize: "40px", fontWeight: 700, letterSpacing: "-0.02em" }}>
                      —
                    </p>
                    <p style={{ color: "#8A8A8A", fontSize: "12px", marginTop: "4px" }}>Total commission (pre-GST)</p>
                  </div>
                )}

                {breakdown && (
                  <>
                    <div className="mt-6">
                      <p style={{ color: "#8A8A8A", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
                        Total commission (pre-GST)
                      </p>
                      <p
                        data-testid="calc-total-pregst"
                        style={{ color: "#4ADE80", fontSize: "42px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1, marginTop: "6px" }}
                      >
                        {fmtINR(breakdown.totalPreGst)}
                      </p>
                    </div>

                    <div className="mt-4 flex items-center justify-between" style={{ paddingBottom: "12px", borderBottom: "1px dashed #1E3A28" }}>
                      <span style={{ color: "#B8B8B8", fontSize: "14px" }}>GST ({gstPct}%)</span>
                      <span data-testid="calc-gst" style={{ color: "#E8E8E8", fontSize: "15px", fontWeight: 600 }}>
                        {fmtINR(breakdown.gstAmount)}
                      </span>
                    </div>

                    <div className="mt-4 flex items-baseline justify-between">
                      <span style={{ color: "#B8B8B8", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
                        Total incl. GST
                      </span>
                      <span data-testid="calc-total-postgst" style={{ color: "#FFFFFF", fontSize: "26px", fontWeight: 700, letterSpacing: "-0.01em" }}>
                        {fmtINR(breakdown.totalPostGst)}
                      </span>
                    </div>

                    {/* ---- PRE SALE CHARGES ---- */}
                    <div className="mt-7">
                      <p style={{ color: "#8A8A8A", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700 }}>
                        Pre sale charges
                      </p>
                      <div className="mt-3 space-y-2">
                        <Row label="Inwarding fee" testid="calc-row-inwarding" value={fmtINR(breakdown.inwardingFee)} sub={`₹${inwardingFeePerUnit.toFixed(2)} / unit × ${quantity || 1}`} />

                        <div>
                          <Row
                            label={
                              <button
                                type="button"
                                onClick={() => setShowStorageTable((v) => !v)}
                                data-testid="calc-toggle-storage"
                                className="flex items-center gap-1.5"
                                style={{ color: "#E8E8E8", background: "transparent", border: "none", padding: 0, cursor: "pointer", fontSize: "14px" }}
                              >
                                Storage fee
                                <ChevronDown
                                  className="w-3.5 h-3.5 transition-transform"
                                  style={{ transform: showStorageTable ? "rotate(180deg)" : "rotate(0deg)", color: "#8A8A8A" }}
                                />
                              </button>
                            }
                            testid="calc-row-storage"
                            value={fmtINR(breakdown.storageFee)}
                            sub={`${breakdown.volumeCubicFt.toFixed(4)} cft × ${days} day${days === 1 ? "" : "s"}`}
                          />
                          {showStorageTable && category && (
                            <div className="mt-2 rounded-lg" style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid #1E3A28", padding: "10px 12px" }}>
                              <table className="w-full" style={{ fontSize: "13px", color: "#B8B8B8" }}>
                                <tbody>
                                  {category.storageBrackets.map((b, i) => {
                                    const from = b.ageFromDay;
                                    const to = b.ageToDay ?? null;
                                    const label = to === null ? `${from}+ days` : from === 1 ? `Up to ${to} days` : `${from}-${to} days`;
                                    return (
                                      <tr key={i}>
                                        <td style={{ padding: "3px 0" }}>{label}</td>
                                        <td style={{ padding: "3px 0", textAlign: "right", color: "#E8E8E8" }}>
                                          ₹{b.perCubicFtPerDay.toFixed(2)}/cft/day
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  <tr>
                                    <td style={{ padding: "3px 0", color: "#8A8A8A", fontSize: "12px" }} colSpan={2}>
                                      Minimum floor: ₹{storageMinPerUnitPerDay.toFixed(2)} per unit per day.
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ---- POST SALE CHARGES ---- */}
                    <div className="mt-6">
                      <p style={{ color: "#8A8A8A", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700 }}>
                        Post sale charges
                      </p>
                      <div className="mt-3 space-y-2">
                        <Row label="Fulfillment fee" testid="calc-row-fulfillment" value={fmtINR(breakdown.fulfillmentFee)} sub="₹50 per order (flat)" />
                        <div>
                          <Row
                            label={
                              <button
                                type="button"
                                onClick={() => setShowSlabTable((v) => !v)}
                                data-testid="calc-toggle-slabs"
                                className="flex items-center gap-1.5"
                                style={{ color: "#E8E8E8", background: "transparent", border: "none", padding: 0, cursor: "pointer", fontSize: "14px" }}
                              >
                                Category commission
                                <ChevronDown
                                  className="w-3.5 h-3.5 transition-transform"
                                  style={{ transform: showSlabTable ? "rotate(180deg)" : "rotate(0deg)", color: "#8A8A8A" }}
                                />
                              </button>
                            }
                            testid="calc-row-commission"
                            value={fmtINR(breakdown.categoryCommission)}
                            sub={`${breakdown.commissionPct}% of ₹${INR.format(sp)} × ${quantity || 1}`}
                          />
                          {showSlabTable && category && (
                            <div className="mt-2 rounded-lg" style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid #1E3A28", padding: "10px 12px" }}>
                              <table className="w-full" style={{ fontSize: "13px" }}>
                                <tbody>
                                  {category.commissionSlabs.length === 1 && category.commissionSlabs[0].max === null ? (
                                    <tr>
                                      <td style={{ padding: "3px 0", color: breakdown.slab === category.commissionSlabs[0] ? "#4ADE80" : "#B8B8B8", fontWeight: breakdown.slab === category.commissionSlabs[0] ? 700 : 400 }}>
                                        Above ₹0
                                      </td>
                                      <td style={{ padding: "3px 0", textAlign: "right", color: breakdown.slab === category.commissionSlabs[0] ? "#4ADE80" : "#E8E8E8", fontWeight: breakdown.slab === category.commissionSlabs[0] ? 700 : 400 }}>
                                        {category.commissionSlabs[0].pct}%
                                      </td>
                                    </tr>
                                  ) : (
                                    category.commissionSlabs.map((s, i) => {
                                      const active = breakdown.slab === s;
                                      const label = s.max === null
                                        ? `Above ₹${INR.format(s.min)}`
                                        : `₹${INR.format(s.min)} to ₹${INR.format(s.max)}`;
                                      return (
                                        <tr key={i}>
                                          <td style={{ padding: "3px 0", color: active ? "#4ADE80" : "#B8B8B8", fontWeight: active ? 700 : 400 }}>
                                            {label}
                                          </td>
                                          <td style={{ padding: "3px 0", textAlign: "right", color: active ? "#4ADE80" : "#E8E8E8", fontWeight: active ? 700 : 400 }}>
                                            {s.pct}%
                                          </td>
                                        </tr>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ---- OTHER CHARGES (below the card) ---- */}
            {breakdown && (
              <div
                className="mt-5 rounded-xl p-5"
                style={{ backgroundColor: "#0F0F0F", border: "1px solid #262626" }}
                data-testid="calc-other-charges"
              >
                <p style={{ color: "#8A8A8A", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700 }}>
                  Other charges (display only, not added to total)
                </p>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <div>
                    <p style={{ color: "#B8B8B8", fontSize: "13px" }}>Inventory recall</p>
                    <p className="mt-1" style={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600 }}>
                      {fmtINR(breakdown.inventoryRecall)}
                    </p>
                    <p style={{ color: "#6B6B6B", fontSize: "11.5px" }}>₹5 / unit</p>
                  </div>
                  <div>
                    <p style={{ color: "#B8B8B8", fontSize: "13px" }}>Customer return</p>
                    <p className="mt-1" style={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600 }}>
                      {fmtINR(breakdown.customerReturn)}
                    </p>
                    <p style={{ color: "#6B6B6B", fontSize: "11.5px" }}>₹50 / qty</p>
                  </div>
                  <div>
                    <p style={{ color: "#B8B8B8", fontSize: "13px" }}>Loss compensation</p>
                    <p className="mt-1" style={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600 }}>
                      Policy
                    </p>
                    <p style={{ color: "#6B6B6B", fontSize: "11.5px" }}>{breakdown.lossCompensationNote}</p>
                  </div>
                </div>
              </div>
            )}

            {/* ---- Disclaimer + CTA row ---- */}
            <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="calc-disclaimer-row">
              <p style={{ color: "#8A8A8A", fontSize: "13px", lineHeight: 1.6, maxWidth: "760px" }}>
                These figures are estimates based on Blinkit&apos;s published commission structure and may change. Rates for some categories are indicative. For exact commercials on your SKUs, book a free audit and we&apos;ll walk you through them.
              </p>
              <button
                type="button"
                onClick={openAuditWhatsApp}
                data-testid="calc-book-audit"
                style={{ ...primaryCtaStyle, height: "44px", padding: "0 18px", fontSize: "14px", whiteSpace: "nowrap" }}
              >
                <MessageCircle className="w-4 h-4" />
                Book a free audit call
              </button>
            </div>
          </div>
        </section>

        {/* ============ UNDERSTANDING BLINKIT COMMISSIONS (accordion) ============ */}
        <section className="py-20" style={{ backgroundColor: "#0F0F0F" }} data-testid="calc-explainer">
          <div className="container">
            <EyebrowLabel>Deep dive</EyebrowLabel>
            <h2
              className="text-white font-semibold"
              style={{ fontSize: "clamp(28px, 3.2vw, 42px)", lineHeight: 1.14, letterSpacing: "-0.02em", maxWidth: "900px" }}
            >
              Understanding Blinkit <span style={{ color: "#F1592B" }}>commissions</span>.
            </h2>
            <p className="mt-5 max-w-[820px]" style={{ color: "#B8B8B8", fontSize: "17px", lineHeight: 1.65 }}>
              Six moving parts add up to what Blinkit actually takes from your top-line. Here is how PickQuick reads each one when we operate a brand.
            </p>

            <div className="mt-10 grid gap-3 max-w-[900px]">
              {EXPLAINER.map((e, i) => (
                <details
                  key={e.q}
                  data-testid={`calc-explainer-${i}`}
                  className="rounded-xl p-5"
                  style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
                >
                  <summary className="cursor-pointer text-white" style={{ fontSize: "16px", fontWeight: 600, listStyle: "none" }}>
                    {e.q}
                  </summary>
                  <p className="mt-3" style={{ color: "#B8B8B8", fontSize: "15px", lineHeight: 1.65 }}>
                    {e.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="py-20" style={{ backgroundColor: "#000000" }} data-testid="calc-faq">
          <div className="container">
            <EyebrowLabel>FAQ</EyebrowLabel>
            <h2
              className="text-white font-semibold"
              style={{ fontSize: "clamp(28px, 3.2vw, 42px)", lineHeight: 1.14, letterSpacing: "-0.02em", maxWidth: "900px" }}
            >
              Blinkit commissions, <span style={{ color: "#F1592B" }}>answered</span>.
            </h2>

            <div className="mt-10 grid gap-3 max-w-[900px]">
              {FAQS.map((f, i) => (
                <details
                  key={f.q}
                  data-testid={`calc-faq-${i}`}
                  className="rounded-xl p-5"
                  style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
                >
                  <summary className="cursor-pointer text-white" style={{ fontSize: "16px", fontWeight: 600, listStyle: "none" }}>
                    {f.q}
                  </summary>
                  <p className="mt-3" style={{ color: "#B8B8B8", fontSize: "15px", lineHeight: 1.65 }}>
                    {f.a}
                  </p>
                </details>
              ))}
            </div>

            {/* Internal cross-links */}
            <div className="mt-14 grid gap-4 md:grid-cols-2 max-w-[900px]" data-testid="calc-cross-links">
              <CrossLink to="/blinkit-seller-onboarding" eyebrow="Platform" title="Blinkit seller onboarding" body="Registration, catalog, pricing, fill-rate, ads and account health, operated end to end." testid="calc-link-primary" primary />
              <CrossLink to="/guides/how-to-sell-on-blinkit" eyebrow="Guide" title="How to sell on Blinkit" body="A walkthrough of Blinkit for FMCG brands, from paperwork to first live shelf." testid="calc-link-how-to" />
              <CrossLink to="/guides/blinkit-vs-zepto" eyebrow="Guide" title="Blinkit vs Zepto" body="Where each platform wins, from PickQuick's operators." testid="calc-link-vs-zepto" />
              <CrossLink to="/qc-consulting" eyebrow="QC Consulting" title="Get an audit of your unit economics" body="PickQuick Intelligence models your Blinkit commercials pincode by pincode." testid="calc-link-consulting" />
            </div>
          </div>
        </section>

        {/* ============ CTA + CONTACT ============ */}
        <section
          id="blinkit-calc-contact"
          className="py-20"
          style={{ backgroundColor: "#0F0F0F" }}
          data-testid="calc-contact"
        >
          <div className="container">
            <EyebrowLabel>Contact</EyebrowLabel>
            <h2
              className="text-white font-semibold"
              style={{ fontSize: "clamp(28px, 3.2vw, 42px)", lineHeight: 1.14, letterSpacing: "-0.02em", maxWidth: "900px" }}
            >
              Want us to run your <span style={{ color: "#F1592B" }}>Blinkit</span> maths on real SKUs?
            </h2>
            <p className="mt-5 max-w-[820px]" style={{ color: "#B8B8B8", fontSize: "17px", lineHeight: 1.65 }}>
              Share your details and we&apos;ll open WhatsApp with the same message pre-filled. We reply within one business day.
            </p>

            <div className="mt-12 grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] max-w-[1180px]">
              <div className="rounded-2xl p-6 md:p-7" style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}>
                <div className="grid gap-4 md:grid-cols-2">
                  <ContactInput id="calc-c-name" testid="calc-contact-name" label="Name *" value={contact.name} onChange={setContactField("name")} placeholder="Your name" />
                  <ContactInput id="calc-c-brand" testid="calc-contact-brand" label="Brand *" value={contact.brand} onChange={setContactField("brand")} placeholder="Brand name" />
                  <ContactInput id="calc-c-email" testid="calc-contact-email" label="Work email *" type="email" value={contact.email} onChange={setContactField("email")} placeholder="you@brand.com" />
                  <ContactInput id="calc-c-phone" testid="calc-contact-phone" label="Phone" value={contact.phone} onChange={setContactField("phone")} placeholder="With country code" />
                </div>
                <button
                  type="button"
                  onClick={sendOnWhatsApp}
                  data-testid="calc-contact-submit"
                  className="mt-6"
                  style={{ ...primaryCtaStyle, width: "100%", justifyContent: "center" }}
                >
                  <MessageCircle className="w-5 h-5" />
                  Send on WhatsApp
                </button>
                <p className="mt-3" style={{ color: "#8A8A8A", fontSize: "13px" }}>
                  Opens WhatsApp with your calculator context pre-filled. We reply within one business day.
                </p>
              </div>

              <div className="rounded-2xl p-6 md:p-7" style={{ backgroundColor: "#1A0B05", border: "1px solid #262626" }}>
                <p className="text-white" style={{ fontSize: "16px", fontWeight: 600 }}>Talk directly to PickQuick</p>
                <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.6 }}>
                  Anuj Sahu, single point of contact for Blinkit commercials.
                </p>
                <div className="mt-5 space-y-3" style={{ fontSize: "14px" }}>
                  <div>
                    <span style={{ color: "#8A8A8A", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.1em" }}>Email</span>
                    <div className="mt-1">
                      <a href="mailto:anuj@letspickquick.com" data-testid="calc-contact-email-link" style={{ color: "#F1592B" }}>
                        anuj@letspickquick.com
                      </a>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: "#8A8A8A", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.1em" }}>Phone / WhatsApp</span>
                    <div className="mt-1">
                      <a href="tel:+918733085863" data-testid="calc-contact-phone-link" style={{ color: "#F1592B" }}>
                        +91 8733085863
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

/* ============================================================================
 * SMALL PRESENTATIONAL HELPERS
 * ==========================================================================*/

const Row = ({ label, value, sub, testid }) => (
  <div className="flex items-start justify-between gap-4" data-testid={testid}>
    <div style={{ minWidth: 0 }}>
      <div style={{ color: "#E8E8E8", fontSize: "14px" }}>{label}</div>
      {sub && <div style={{ color: "#6B6B6B", fontSize: "12px", marginTop: "2px" }}>{sub}</div>}
    </div>
    <div style={{ color: "#FFFFFF", fontSize: "15px", fontWeight: 600, whiteSpace: "nowrap" }}>{value}</div>
  </div>
);

const CrossLink = ({ to, eyebrow, title, body, testid, primary = false }) => (
  <Link
    to={to}
    data-testid={testid}
    className="rounded-xl p-5"
    style={{
      backgroundColor: primary ? "rgba(241,89,43,0.08)" : "#1A1A1A",
      border: primary ? "1px solid rgba(241,89,43,0.35)" : "1px solid #262626",
      textDecoration: "none",
      display: "block",
    }}
  >
    <p style={{ color: "#F1592B", fontSize: "12px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
      {eyebrow}
    </p>
    <p className="mt-2 text-white" style={{ fontSize: "16px", fontWeight: 600 }}>{title}</p>
    <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.55 }}>{body}</p>
    <p className="mt-3 inline-flex items-center gap-1" style={{ color: "#F1592B", fontSize: "13px", fontWeight: 600 }}>
      Read <ArrowRight className="w-3.5 h-3.5" />
    </p>
  </Link>
);

const ContactInput = ({ id, testid, label, type = "text", value, onChange, placeholder }) => (
  <div>
    <label htmlFor={id} className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
      {label}
    </label>
    <input
      id={id}
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={inputStyle}
    />
  </div>
);

export default BlinkitCommissionStructure;
